import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Phone, PhoneCall, Database, CalendarCheck, Brain, Check, Sparkles,
  Mail, MessageSquare, Clock, User, Activity, ArrowUpRight, TrendingUp,
  Tag, Zap, Languages, Bell, Calendar, ShieldCheck, Hash
} from "lucide-react";
import { cn } from "@/lib/utils";
import xpandOsLogo from "@/assets/xpand-os-logo.png";

const SCENE_DURATION = 5200;
const SCENES = ["call", "crm", "booking", "memory"] as const;
type Scene = typeof SCENES[number];

const SCENE_THEME: Record<Scene, { glow: string; gradient: string; accent: string; chip: string; ring: string }> = {
  call:    { glow: "200 95% 60%", gradient: "from-sky-400 to-blue-500",      accent: "text-sky-300",    chip: "bg-sky-500/10 border-sky-500/20 text-sky-300",       ring: "sky-400" },
  crm:     { glow: "270 90% 65%", gradient: "from-violet-400 to-purple-500", accent: "text-violet-300", chip: "bg-violet-500/10 border-violet-500/20 text-violet-300", ring: "violet-400" },
  booking: { glow: "320 85% 65%", gradient: "from-pink-400 to-fuchsia-500",  accent: "text-pink-300",   chip: "bg-pink-500/10 border-pink-500/20 text-pink-300",     ring: "pink-400" },
  memory:  { glow: "230 95% 60%", gradient: "from-indigo-400 to-blue-500",   accent: "text-indigo-300", chip: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300", ring: "indigo-400" },
};

export function HeroFlowLoop() {
  const { t } = useTranslation();
  const [sceneIdx, setSceneIdx] = useState(0);
  const scene = SCENES[sceneIdx];
  const theme = SCENE_THEME[scene];

  useEffect(() => {
    const id = setInterval(() => setSceneIdx((p) => (p + 1) % SCENES.length), SCENE_DURATION);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative w-full max-w-[560px] sm:max-w-xl mx-auto lg:max-w-none">
      {/* Ambient glow */}
      <div
        className="absolute -inset-6 rounded-[2rem] pointer-events-none transition-all duration-[1200ms]"
        style={{
          background: `radial-gradient(ellipse at center, hsl(${theme.glow} / 0.28), transparent 70%)`,
          filter: "blur(70px)",
        }}
      />

      {/* Outer card */}
      <div className="relative rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] backdrop-blur-md overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        {/* Top bar */}
        <div className="relative flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/15" />
            </div>
            <div className="h-3 w-px bg-white/10" />
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-white/55 font-medium">
              {t("hero.flow.live")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-white/40 font-mono tracking-wider hidden sm:inline">{t("hero.flow.region")}</span>
            <span className="text-[9px] text-white/40">·</span>
            <span className="text-[9px] text-white/45 font-mono tracking-wider">{t("hero.flow.system")}</span>
          </div>
        </div>

        {/* Stage area – taller, denser */}
        <div className="relative h-[440px] sm:h-[460px] overflow-hidden">
          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
              backgroundSize: "28px 28px",
              maskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
              WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
            }}
          />
          {/* Soft scene tint */}
          <div
            className="absolute inset-0 pointer-events-none transition-all duration-1000"
            style={{ background: `radial-gradient(ellipse at 70% 20%, hsl(${theme.glow} / 0.10), transparent 60%)` }}
          />

          <SceneWrapper active={scene === "call"}><CallScene t={t} /></SceneWrapper>
          <SceneWrapper active={scene === "crm"}><CrmScene t={t} /></SceneWrapper>
          <SceneWrapper active={scene === "booking"}><BookingScene t={t} /></SceneWrapper>
          <SceneWrapper active={scene === "memory"}><MemoryScene t={t} /></SceneWrapper>
        </div>

        {/* Footer: scene tabs + progress */}
        <div className="relative px-4 pt-2.5 pb-3 border-t border-white/[0.06]">
          <div className="flex items-center justify-between gap-1.5 mb-2.5">
            {SCENES.map((s, i) => {
              const isActive = i === sceneIdx;
              const Icon = s === "call" ? Phone : s === "crm" ? Database : s === "booking" ? CalendarCheck : Brain;
              return (
                <div
                  key={s}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md transition-all duration-500",
                    isActive ? "bg-white/[0.05]" : "opacity-40"
                  )}
                >
                  <Icon className={cn("w-3 h-3", isActive ? SCENE_THEME[s].accent : "text-white/40")} />
                  <span className={cn("text-[10px] font-medium hidden sm:block", isActive ? "text-white/85" : "text-white/40")}>
                    {t(`hero.flow.${s}.title`)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="relative h-[3px] rounded-full bg-white/[0.04] overflow-hidden">
            <div
              key={sceneIdx}
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                background: `linear-gradient(to right, hsl(${theme.glow}), hsl(${theme.glow} / 0.4))`,
                animation: `hero-progress ${SCENE_DURATION}ms linear forwards`,
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hero-progress { from { width: 0%; } to { width: 100%; } }
        @keyframes scene-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ring-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: 0.85; } }
        @keyframes ring-wave { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes wave-bar { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
        @keyframes type-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slot-fill { from { width: 0%; } to { width: 100%; } }
        @keyframes pop-in { 0% { opacity: 0; transform: scale(0.92); } 60% { opacity: 1; transform: scale(1.02); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes float-up { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes slide-down { 0% { opacity: 0; transform: translateY(-8px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes brain-pulse { 0%, 100% { box-shadow: 0 0 0 0 hsl(230 95% 60% / 0.45); } 50% { box-shadow: 0 0 0 14px hsl(230 95% 60% / 0); } }
        @keyframes orbit { from { transform: rotate(0deg) translateX(46px) rotate(0deg); } to { transform: rotate(360deg) translateX(46px) rotate(-360deg); } }
        @keyframes caret-blink { 50% { opacity: 0; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes counter-up { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes node-ping { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
      `}</style>
    </div>
  );
}

/* ─── Scene wrapper ─── */
function SceneWrapper({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "absolute inset-0 transition-opacity duration-500",
        active ? "opacity-100 z-10" : "opacity-0 pointer-events-none z-0"
      )}
    >
      {active && children}
    </div>
  );
}

/* ─── SCENE 1: Voice AI – multi-call queue + active conversation ─── */
function CallScene({ t }: { t: (k: string) => string }) {
  const [answered, setAnswered] = useState(false);
  const [transcriptStep, setTranscriptStep] = useState(0);

  useEffect(() => {
    setAnswered(false);
    setTranscriptStep(0);
    const t1 = setTimeout(() => setAnswered(true), 1100);
    const t2 = setTimeout(() => setTranscriptStep(1), 2200);
    const t3 = setTimeout(() => setTranscriptStep(2), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const queue = [
    { name: "Marco D.", meta: "+39 06 555", tag: t("hero.flow.call.queueTag1") },
    { name: "Lina K.",  meta: "+44 20 555", tag: t("hero.flow.call.queueTag2") },
  ];

  const transcript = [
    { who: t("hero.flow.call.callerShort"), text: t("hero.flow.call.line1") },
    { who: t("hero.flow.call.agent"),       text: t("hero.flow.call.line2") },
    { who: t("hero.flow.call.callerShort"), text: t("hero.flow.call.line3") },
  ];

  return (
    <div className="absolute inset-0 p-4 flex flex-col gap-3" style={{ animation: "scene-in 0.5s ease-out both" }}>
      {/* Header row with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-sky-300" />
          <span className="text-[11px] font-medium text-white/85">{t("hero.flow.call.title")}</span>
          <span className="text-[10px] text-white/35">·</span>
          <span className="text-[10px] text-white/50">{t("hero.flow.call.subtitle")}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[9px] font-mono text-white/45">
          <Activity className="w-3 h-3 text-emerald-400" />
          <span>{t("hero.flow.call.liveCalls")}</span>
        </div>
      </div>

      {/* Active call card */}
      <div className="relative rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 overflow-hidden">
        {/* shimmer */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-sky-400/10 to-transparent" style={{ animation: "shimmer 3s ease-in-out infinite" }} />
        </div>

        <div className="relative flex items-center gap-3">
          <div className="relative shrink-0">
            {!answered && (
              <>
                <span className="absolute inset-0 rounded-full bg-sky-400/30" style={{ animation: "ring-wave 1.4s ease-out infinite" }} />
                <span className="absolute inset-0 rounded-full bg-sky-400/30" style={{ animation: "ring-wave 1.4s ease-out 0.5s infinite" }} />
              </>
            )}
            <div
              className={cn(
                "relative w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500",
                answered ? "bg-gradient-to-br from-emerald-400 to-emerald-600" : "bg-gradient-to-br from-sky-400 to-blue-500"
              )}
              style={{
                animation: answered ? undefined : "ring-pulse 1.2s ease-in-out infinite",
                boxShadow: answered ? "0 0 22px hsl(150 80% 50% / 0.5)" : "0 0 22px hsl(200 95% 60% / 0.5)",
              }}
            >
              {answered ? <Check className="w-5 h-5 text-white" /> : <PhoneCall className="w-5 h-5 text-white" />}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-white truncate">{t("hero.flow.call.caller")}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 font-medium">VIP</span>
            </div>
            <div className="text-[11px] text-white/50 truncate">{t("hero.flow.call.callerMeta")}</div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span
              className={cn(
                "text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium transition-all duration-500",
                answered ? "bg-emerald-500/20 text-emerald-300" : "bg-sky-500/20 text-sky-300"
              )}
            >
              {answered ? t("hero.flow.call.answered") : t("hero.flow.call.status")}
            </span>
            <span className="text-[9px] text-white/40 font-mono flex items-center gap-1">
              <Languages className="w-2.5 h-2.5" /> EN · ES
            </span>
          </div>
        </div>

        {/* Waveform */}
        {answered && (
          <div className="relative mt-3 flex items-end justify-center gap-[3px] h-7" style={{ animation: "type-fade 0.4s ease-out both" }}>
            {Array.from({ length: 38 }).map((_, i) => (
              <span
                key={i}
                className="w-[2px] bg-gradient-to-t from-sky-500 to-cyan-300 rounded-full"
                style={{
                  height: `${14 + Math.sin(i * 0.6) * 12}px`,
                  animation: `wave-bar ${0.55 + (i % 5) * 0.11}s ease-in-out ${i * 0.035}s infinite`,
                  transformOrigin: "bottom",
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Live transcript – multi-line */}
      <div className="relative rounded-xl border border-white/[0.06] bg-black/20 p-3 flex-1 overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.18em] text-sky-300/90 font-medium">{t("hero.flow.call.transcriptLabel")}</span>
          <span className="text-[9px] text-white/35 font-mono">75ms</span>
        </div>
        <div className="space-y-1.5">
          {transcript.slice(0, transcriptStep + 1).map((line, i) => (
            <div key={i} className="flex gap-2 text-[11px] leading-snug" style={{ animation: "type-fade 0.35s ease-out both" }}>
              <span className={cn("font-medium shrink-0 w-12", line.who === t("hero.flow.call.agent") ? "text-sky-300" : "text-white/55")}>{line.who}</span>
              <span className="text-white/85">
                {line.text}
                {i === transcriptStep && <span className="inline-block w-[6px] h-3 ml-0.5 align-middle bg-sky-300/80" style={{ animation: "caret-blink 1s steps(1) infinite" }} />}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Queue */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] uppercase tracking-[0.18em] text-white/45 font-medium">{t("hero.flow.call.queueLabel")}</span>
          <span className="text-[9px] text-white/35 font-mono">2 {t("hero.flow.call.waiting")}</span>
        </div>
        <div className="space-y-1">
          {queue.map((q, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px]" style={{ animation: `slide-down 0.4s ease-out ${0.4 + i * 0.15}s both` }}>
              <span className="w-1 h-1 rounded-full bg-sky-400 shrink-0" />
              <span className="text-white/80 font-medium">{q.name}</span>
              <span className="text-white/35 font-mono">{q.meta}</span>
              <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/55 border border-white/[0.06]">{q.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── SCENE 2: Smart CRM – field cascade + score + pipeline ─── */
function CrmScene({ t }: { t: (k: string) => string }) {
  const fields = [
    { label: t("hero.flow.crm.field1"), value: t("hero.flow.crm.field1Value"), icon: User },
    { label: t("hero.flow.crm.field2"), value: t("hero.flow.crm.field2Value"), icon: Phone },
    { label: t("hero.flow.crm.field3"), value: t("hero.flow.crm.field3Value"), icon: Sparkles },
    { label: t("hero.flow.crm.field4"), value: t("hero.flow.crm.field4Value"), icon: Database },
  ];

  const pipeline = [
    { label: t("hero.flow.crm.stage1"), value: 100, active: true,  done: true,  count: 248 },
    { label: t("hero.flow.crm.stage2"), value: 100, active: true,  done: true,  count: 142 },
    { label: t("hero.flow.crm.stage3"), value: 65,  active: true,  done: false, count: 94 },
    { label: t("hero.flow.crm.stage4"), value: 0,   active: false, done: false, count: 38 },
  ];

  // Sparkline points (call volume last 12 hrs)
  const spark = [12, 18, 14, 22, 28, 24, 32, 38, 34, 42, 48, 54];
  const sparkMax = Math.max(...spark);
  const sparkPath = spark
    .map((v, i) => `${(i / (spark.length - 1)) * 100},${30 - (v / sparkMax) * 26}`)
    .join(" ");

  const metrics = [
    { label: t("hero.flow.crm.mTotalCalls"), value: "1,284", icon: PhoneCall, accent: "text-violet-300", trend: "+18%" },
    { label: t("hero.flow.crm.mAvgDuration"), value: "2:14", icon: Clock, accent: "text-sky-300", trend: "−6%" },
    { label: t("hero.flow.crm.mLeads"), value: "342", icon: User, accent: "text-fuchsia-300", trend: "+24%" },
    { label: t("hero.flow.crm.mSuccess"), value: "38%", icon: TrendingUp, accent: "text-emerald-300", trend: "+4pt" },
  ];

  return (
    <div className="absolute inset-0 px-5 py-4 flex flex-col gap-3.5" style={{ animation: "scene-in 0.5s ease-out both" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Database className="w-3.5 h-3.5 text-violet-300 shrink-0" />
          <span className="text-[11px] font-medium text-white/85 truncate">{t("hero.flow.crm.title")}</span>
          <span className="text-[10px] text-white/30">·</span>
          <span className="text-[10px] text-white/45 truncate">{t("hero.flow.crm.subtitle")}</span>
        </div>
        <div className="flex items-center gap-1 text-[9px] font-mono text-emerald-300/90 shrink-0">
          <ArrowUpRight className="w-3 h-3" />
          <span>+12 {t("hero.flow.crm.today")}</span>
        </div>
      </div>

      {/* Lead score banner – sleeker, taller, breathable */}
      <div
        className="rounded-xl border border-violet-500/15 bg-gradient-to-r from-violet-500/[0.08] via-fuchsia-500/[0.04] to-transparent px-4 py-3 flex items-center gap-3.5"
        style={{ animation: "pop-in 0.5s ease-out 0.05s both" }}
      >
        <div className="relative w-11 h-11 shrink-0">
          <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(0 0% 100% / 0.06)" strokeWidth="2.5" />
            <circle cx="18" cy="18" r="15" fill="none" stroke="url(#crmGrad)" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray="94" strokeDashoffset="94" style={{ animation: "crm-score 1s ease-out 0.2s forwards" }} />
            <defs>
              <linearGradient id="crmGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="hsl(280 90% 70%)" />
                <stop offset="100%" stopColor="hsl(330 85% 65%)" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white" style={{ animation: "counter-up 0.6s ease-out 0.5s both" }}>92</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium text-white truncate">{t("hero.flow.crm.scoreTitle")}</div>
          <div className="text-[10px] text-white/45 truncate mt-0.5">{t("hero.flow.crm.scoreSub")}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[9px] font-medium px-2 py-1 rounded-md bg-rose-500/15 text-rose-300 border border-rose-500/20 flex items-center gap-1">
            <Zap className="w-2.5 h-2.5" /> {t("hero.flow.crm.tagHot")}
          </span>
          <span className="text-[9px] font-medium px-2 py-1 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/20 hidden sm:flex items-center gap-1">
            <Tag className="w-2.5 h-2.5" /> {t("hero.flow.crm.tagNew")}
          </span>
        </div>
        <style>{`@keyframes crm-score { to { stroke-dashoffset: 8; } }`}</style>
      </div>

      {/* Fields cascade – 2 cols, more breathing room */}
      <div className="grid grid-cols-2 gap-2">
        {fields.map((f, i) => {
          const Icon = f.icon;
          return (
            <div
              key={i}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02]"
              style={{ animation: `float-up 0.4s ease-out ${0.2 + i * 0.08}s both` }}
            >
              <div className="w-6 h-6 rounded-md bg-violet-500/10 border border-violet-500/15 flex items-center justify-center shrink-0">
                <Icon className="w-3 h-3 text-violet-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[8px] text-white/35 uppercase tracking-wider leading-none">{f.label}</div>
                <div className="text-[10.5px] text-white/90 font-medium truncate leading-tight mt-0.5">{f.value}</div>
              </div>
              <Check className="w-3 h-3 text-emerald-400 shrink-0" />
            </div>
          );
        })}
      </div>

      {/* Sparkline – taller, full width */}
      <div className="rounded-xl border border-white/[0.05] bg-white/[0.015] px-4 py-3" style={{ animation: "float-up 0.4s ease-out 0.5s both" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.18em] text-white/45 font-medium flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-violet-300" /> {t("hero.flow.crm.analyticsLabel")}
          </span>
          <span className="text-[9px] text-emerald-300/90 font-mono flex items-center gap-1">
            <ArrowUpRight className="w-2.5 h-2.5" /> +24%
          </span>
        </div>
        <div className="relative h-10">
          <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
            <defs>
              <linearGradient id="crmSparkFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(280 90% 70%)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="hsl(280 90% 70%)" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="crmSparkLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="hsl(280 90% 70%)" />
                <stop offset="100%" stopColor="hsl(330 85% 65%)" />
              </linearGradient>
            </defs>
            <polygon points={`0,30 ${sparkPath} 100,30`} fill="url(#crmSparkFill)" />
            <polyline points={sparkPath} fill="none" stroke="url(#crmSparkLine)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: "spark-draw 1.4s ease-out 0.6s forwards" }} />
            <circle cx="98" cy={30 - (spark[spark.length - 1] / sparkMax) * 26} r="1.4" fill="hsl(330 85% 65%)" />
          </svg>
          <style>{`@keyframes spark-draw { to { stroke-dashoffset: 0; } }`}</style>
        </div>
      </div>

      {/* Performance metrics – full-width strip with dividers */}
      <div className="rounded-xl border border-white/[0.05] bg-white/[0.015] py-3 grid grid-cols-4 divide-x divide-white/[0.05]">
        {metrics.map((m, i) => {
          const Icon = m.icon;
          const trendUp = !m.trend.startsWith("−");
          return (
            <div
              key={i}
              className="px-3 flex flex-col gap-1"
              style={{ animation: `float-up 0.4s ease-out ${0.6 + i * 0.07}s both` }}
            >
              <div className="flex items-center justify-between">
                <Icon className={cn("w-3 h-3", m.accent)} />
                <span className={cn("text-[8px] font-mono", trendUp ? "text-emerald-300/80" : "text-rose-300/80")}>{m.trend}</span>
              </div>
              <div className="text-[15px] font-semibold text-white font-mono leading-none mt-0.5 tracking-tight">{m.value}</div>
              <div className="text-[8px] text-white/40 uppercase tracking-wider truncate leading-tight">{m.label}</div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

/* ─── SCENE 3: Smart Booking – calendar + slots + bookings list ─── */
function BookingScene({ t }: { t: (k: string) => string }) {
  const slots = ["6:00", "6:30", "7:00", "7:30", "8:00", "8:30"];
  const targetIdx = 2;

  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const dayNums = [21, 22, 23, 24, 25, 26, 27];
  const targetDay = 4;

  const upcoming = [
    { time: "12:30", name: t("hero.flow.booking.up1Name"), guests: "2" },
    { time: "19:00", name: t("hero.flow.booking.caller"),  guests: "4", active: true },
    { time: "21:15", name: t("hero.flow.booking.up3Name"), guests: "6" },
  ];

  return (
    <div className="absolute inset-0 p-4 flex flex-col gap-3" style={{ animation: "scene-in 0.5s ease-out both" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-3.5 h-3.5 text-pink-300" />
          <span className="text-[11px] font-medium text-white/85">{t("hero.flow.booking.title")}</span>
          <span className="text-[10px] text-white/35">·</span>
          <span className="text-[10px] text-white/50">{t("hero.flow.booking.subtitle")}</span>
        </div>
        <span className="text-[9px] text-white/45 font-mono flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {t("hero.flow.booking.month")}</span>
      </div>

      {/* Mini week calendar */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-2.5">
        <div className="grid grid-cols-7 gap-1 mb-1.5">
          {days.map((d, i) => (
            <div key={i} className="text-[9px] text-center text-white/35 uppercase tracking-wider">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {dayNums.map((n, i) => (
            <div key={i}
              className={cn(
                "relative h-7 rounded-md text-[10px] flex items-center justify-center font-medium border transition-colors",
                i === targetDay
                  ? "border-pink-500/40 text-white bg-gradient-to-br from-pink-500/25 to-fuchsia-500/15"
                  : "border-white/[0.04] bg-white/[0.015] text-white/55"
              )}
              style={i === targetDay ? { animation: "pop-in 0.5s ease-out 0.2s both" } : undefined}
            >
              {n}
              {i === targetDay && (
                <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-pink-400" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time slots row */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] uppercase tracking-[0.18em] text-pink-300/90 font-medium">{t("hero.flow.booking.slotsLabel")}</span>
          <span className="text-[9px] text-white/40 font-mono">{t("hero.flow.booking.day")} · {t("hero.flow.booking.date")}</span>
        </div>
        <div className="grid grid-cols-6 gap-1">
          {slots.map((s, i) => {
            const isTarget = i === targetIdx;
            const isTaken = i === 0 || i === 4;
            return (
              <div
                key={s}
                className={cn(
                  "relative h-9 rounded-md text-[10px] font-medium flex items-center justify-center overflow-hidden border",
                  isTarget
                    ? "border-pink-500/40 text-white"
                    : isTaken
                    ? "border-white/[0.04] bg-white/[0.01] text-white/25 line-through"
                    : "border-white/[0.06] bg-white/[0.02] text-white/50"
                )}
                style={isTarget ? { animation: "pop-in 0.5s ease-out 0.4s both" } : undefined}
              >
                {isTarget && (
                  <span
                    className="absolute inset-0 bg-gradient-to-r from-pink-500/40 to-fuchsia-500/40"
                    style={{ animation: "slot-fill 0.7s ease-out 0.5s both" }}
                  />
                )}
                <span className="relative z-10">{s}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming bookings list */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-2.5 flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.18em] text-white/45 font-medium">{t("hero.flow.booking.todayLabel")}</span>
          <span className="text-[9px] text-emerald-300/90 font-mono">3 {t("hero.flow.booking.confirmed")}</span>
        </div>
        <div className="space-y-1.5">
          {upcoming.map((b, i) => (
            <div key={i}
              className={cn(
                "flex items-center gap-2.5 px-2 py-1.5 rounded-md border",
                b.active ? "border-pink-500/25 bg-pink-500/[0.06]" : "border-white/[0.05] bg-white/[0.015]"
              )}
              style={{ animation: `slide-down 0.4s ease-out ${0.7 + i * 0.12}s both` }}
            >
              <Clock className={cn("w-3 h-3", b.active ? "text-pink-300" : "text-white/40")} />
              <span className="text-[11px] font-mono text-white/80 w-10">{b.time}</span>
              <span className="text-[11px] text-white/85 flex-1 truncate">{b.name}</span>
              <span className="text-[9px] text-white/45 flex items-center gap-1"><User className="w-2.5 h-2.5" />{b.guests}</span>
              {b.active && <Check className="w-3 h-3 text-emerald-400" />}
            </div>
          ))}
        </div>
      </div>

      {/* Notify */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500/[0.08] to-transparent border border-emerald-500/20"
        style={{ animation: "float-up 0.4s ease-out 1.1s both" }}
      >
        <div className="flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3 text-emerald-300" />
          <Mail className="w-3 h-3 text-emerald-300" />
        </div>
        <span className="text-[10px] text-white/75 flex-1">{t("hero.flow.booking.notify")}</span>
        <Bell className="w-3 h-3 text-emerald-300" />
      </div>
    </div>
  );
}

/* ─── SCENE 4: XPAND OS Memory – live recall demo ─── */
function MemoryScene({ t }: { t: (k: string) => string }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    const t1 = setTimeout(() => setStep(1), 900);
    const t2 = setTimeout(() => setStep(2), 1900);
    const t3 = setTimeout(() => setStep(3), 2900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const facts = [
    { label: t("hero.flow.memory.fact1"), icon: ShieldCheck },
    { label: t("hero.flow.memory.fact2"), icon: Sparkles },
    { label: t("hero.flow.memory.fact3"), icon: Clock },
    { label: t("hero.flow.memory.fact4"), icon: Tag },
  ];

  return (
    <div className="absolute inset-0 p-4 flex flex-col gap-2.5" style={{ animation: "scene-in 0.5s ease-out both" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={xpandOsLogo} alt="" className="h-3 w-auto opacity-90" />
          <span className="text-[11px] font-medium text-white/85">{t("hero.flow.memory.title")}</span>
          <span className="text-[10px] text-white/35">·</span>
          <span className="text-[10px] text-white/50">{t("hero.flow.memory.subtitle")}</span>
        </div>
        <span className="text-[9px] text-white/45 font-mono flex items-center gap-1"><Hash className="w-2.5 h-2.5" />CUST-1042</span>
      </div>

      {/* Recall demo: breathing XPAND OS core + recall steps */}
      <div className="relative rounded-xl border border-white/[0.06] bg-black/20 p-4 overflow-hidden">
        {/* Flowing breathing aura with XPAND OS logo */}
        <div className="relative h-[140px] flex items-center justify-center">
          {/* Outer flowing aura layers */}
          <div
            className="absolute rounded-full"
            style={{
              width: "180px", height: "180px",
              background: "radial-gradient(circle, hsl(230 95% 60% / 0.35), hsl(270 90% 65% / 0.18) 45%, transparent 72%)",
              filter: "blur(28px)",
              animation: "xos-breath-slow 5s ease-in-out infinite",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: "130px", height: "130px",
              background: "radial-gradient(circle, hsl(260 95% 70% / 0.5), transparent 70%)",
              filter: "blur(18px)",
              animation: "xos-breath 3.5s ease-in-out infinite",
            }}
          />
          {/* Soft inner halo ring */}
          <div
            className="absolute rounded-full border border-indigo-300/15"
            style={{
              width: "110px", height: "110px",
              animation: "xos-halo 4s ease-in-out infinite",
            }}
          />
          <div
            className="absolute rounded-full border border-violet-300/10"
            style={{
              width: "150px", height: "150px",
              animation: "xos-halo 4s ease-in-out infinite",
              animationDelay: "0.8s",
            }}
          />
          {/* Drifting sparkles */}
          {[
            { top: "18%", left: "22%", d: "0s" },
            { top: "30%", right: "20%", d: "1.1s" },
            { bottom: "20%", left: "28%", d: "2.2s" },
            { bottom: "26%", right: "26%", d: "0.6s" },
          ].map((s, i) => (
            <span
              key={i}
              className="absolute w-1 h-1 rounded-full bg-indigo-200"
              style={{
                top: s.top, left: s.left, right: s.right, bottom: s.bottom,
                boxShadow: "0 0 6px hsl(230 95% 70% / 0.9)",
                animation: "xos-drift 3.5s ease-in-out infinite",
                animationDelay: s.d,
              }}
            />
          ))}
          {/* XPAND OS logo – breathing heart */}
          <img
            src={xpandOsLogo}
            alt="XPAND OS"
            className="relative z-10 w-[88px] h-auto select-none"
            style={{
              animation: "xos-heartbeat 2.4s ease-in-out infinite",
              filter: "drop-shadow(0 0 18px hsl(260 95% 65% / 0.55))",
            }}
            draggable={false}
          />
        </div>

        {/* Recall steps */}
        <div className="space-y-1.5 mt-2">
          <RecallLine active={step >= 0} done={step >= 1} label={t("hero.flow.memory.step1")} value="+1 (415) 555 · CUST-1042" />
          <RecallLine active={step >= 1} done={step >= 2} label={t("hero.flow.memory.step2")} value={`${t("hero.flow.crm.field1Value")} · ${t("hero.flow.memory.visits")}`} />
          <RecallLine active={step >= 2} done={step >= 3} label={t("hero.flow.memory.step3")} value={t("hero.flow.memory.greeting")} highlight />
        </div>
      </div>

      {/* Memory facts grid */}
      <div className="grid grid-cols-2 gap-1.5">
        {facts.map((f, i) => {
          const Icon = f.icon;
          return (
            <div
              key={i}
              className="px-2.5 py-2 rounded-lg border border-indigo-500/15 bg-gradient-to-br from-indigo-500/[0.06] to-transparent text-[10px] text-white/85 flex items-center gap-2"
              style={{ animation: `pop-in 0.4s ease-out ${0.3 + i * 0.12}s both` }}
            >
              <div className="w-5 h-5 rounded bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center shrink-0">
                <Icon className="w-2.5 h-2.5 text-indigo-300" />
              </div>
              <span className="truncate">{f.label}</span>
            </div>
          );
        })}
      </div>

      {/* Footer recall stat */}
      <div
        className="mt-auto flex items-center justify-between px-3 py-2 rounded-lg bg-indigo-500/[0.06] border border-indigo-500/20"
        style={{ animation: "float-up 0.4s ease-out 1s both" }}
      >
        <span className="text-[10px] uppercase tracking-wider text-indigo-300 font-medium">
          {t("hero.flow.memory.recallLabel")}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-[9px] text-white/55 font-mono">218 {t("hero.flow.memory.facts")}</span>
          <span className="text-[10px] text-white/65 font-mono">∞ persistent</span>
        </div>
      </div>

      {/* Local breathing keyframes */}
      <style>{`
        @keyframes xos-heartbeat {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 14px hsl(260 95% 65% / 0.45)); }
          15%      { transform: scale(1.08); filter: drop-shadow(0 0 26px hsl(260 95% 70% / 0.75)); }
          30%      { transform: scale(1.02); }
          45%      { transform: scale(1.06); filter: drop-shadow(0 0 22px hsl(260 95% 70% / 0.65)); }
          60%      { transform: scale(1); }
        }
        @keyframes xos-breath {
          0%, 100% { transform: scale(0.9); opacity: 0.65; }
          50%      { transform: scale(1.15); opacity: 1; }
        }
        @keyframes xos-breath-slow {
          0%, 100% { transform: scale(0.95); opacity: 0.55; }
          50%      { transform: scale(1.1);  opacity: 0.85; }
        }
        @keyframes xos-halo {
          0%, 100% { transform: scale(0.92); opacity: 0.55; }
          50%      { transform: scale(1.08); opacity: 0.9; }
        }
        @keyframes xos-drift {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
          50%      { transform: translateY(-6px) scale(1.4); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function RecallLine({ active, done, label, value, highlight }: { active: boolean; done: boolean; label: string; value: string; highlight?: boolean }) {
  return (
    <div className={cn("flex items-center gap-2 text-[10px] transition-opacity duration-300", active ? "opacity-100" : "opacity-30")}>
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        {active && !done && <span className="absolute inset-0 rounded-full bg-indigo-400" style={{ animation: "node-ping 1.2s ease-out infinite" }} />}
        <span className={cn("relative inline-flex h-1.5 w-1.5 rounded-full", done ? "bg-emerald-400" : active ? "bg-indigo-400" : "bg-white/20")} />
      </span>
      <span className="text-white/45 uppercase tracking-wider w-20 shrink-0 text-[8px]">{label}</span>
      <span className={cn("flex-1 truncate font-medium", highlight ? "text-indigo-200 italic" : "text-white/85")}>{value}</span>
      {done && <Check className="w-3 h-3 text-emerald-400 shrink-0" />}
    </div>
  );
}

export default HeroFlowLoop;
